import dayjs from 'dayjs';
import logger from '../../utils/logger';
import tz from 'dayjs/plugin/timezone';
import { createAction, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getCart } from '../../apis/cart';
import { Quote } from '../../types/quotes';
import {
  CartModalPayload,
  CartState,
  UpdateRentalProtectionPayload,
  UpdateShiftPayload,
  SetCartHeaderOpenPayload,
  SetUpdateCartBackendStatusPayload,
  SetUserUuidPayload,
  UpdateCartBackendPayload,
  UpdateActiveRentalProtectionPayload,
  UpdateActiveShiftPayload,
  SiteLevelDate,
  RefreshCartItemsValidityPayload,
  ValidateCartDatesPayload,
  SalesStaffDetails,
} from '../../types/cart-state';
import {
  CartItem,
  CartModalConfigType,
  CartOperationType,
  CartStateLoadingType,
} from '../../types/cart';

dayjs.extend(tz);

const initialState: CartState = {
  loading: CartStateLoadingType.IDLE,
  backendCartUpdateStatus: null,
  userUuid: null,
  cartItems: [],
  timezone: dayjs.tz.guess(),
  areCartItemsValid: true,
  isTaxExempt: false,
  isLoadingFlags: {
    quote: false,
    isTaxExempt: false
  }
};

export const validateCartItemsData = createAsyncThunk(
  'cartSlice/validateCartItemsData',
  async (userUuid: string | null) => {
    if (userUuid === null) return [];
    const response = await getCart(userUuid);
    const cartItemsResponse = response?.cart_details;
    let cartItems: CartItem[] | undefined = [];
    cartItems = cartItemsResponse?.map((item) => {
      const cartItem: CartItem = {
        equipmentId: item.equipment_class_id,
        name: item.equipment_name,
        imageSource: item.equipment_img_url,
        quantity: item.quantity,
        startDate: item.delivery_date,
        endDate: item.return_date,
        slug: item.slug,
      };
      return cartItem;
    });
    return cartItems;
  },
);

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    openCartModal: (state, action: PayloadAction<CartModalPayload>) => {
      const cartModalConfig: CartModalConfigType = {
        equipmentId: action.payload.cartItem.equipmentId,
        cartOperationType: action.payload.cartOperationType,
        imageSource: action.payload.cartItem.imageSource,
        equipmentClassName: action.payload.cartItem.name,
        quantity: action.payload.cartItem.quantity ?? 1,
        slug: action.payload.cartItem.slug,
        open: true,
        toggleCartHeaderOnUpdate: action.payload.toggleCartHeaderOnUpdate,
      };
      if (action.payload.cartOperationType === CartOperationType.UPDATE) {
        cartModalConfig.index = action.payload.index;
        cartModalConfig.startDate = action.payload.cartItem.startDate;
        cartModalConfig.endDate = action.payload.cartItem.endDate;
      }
      state.cartModalConfig = cartModalConfig;
      state.cartHeaderConfig = undefined;
    },
    closeCartModal: (state) => {
      state.cartModalConfig = undefined;
    },
    toggleCartHeaderDropdown: (state, action: PayloadAction<SetCartHeaderOpenPayload>) => {
      state.cartHeaderConfig = {
        open: action.payload.open,
        index: action.payload.index,
        maxItemsToShow: 3,
      };
    },
    setUserUuid: (state, action: PayloadAction<SetUserUuidPayload>) => {
      state.userUuid = action.payload.id;
    },
    setUpdateCartBackendStatus: (
      state,
      action: PayloadAction<SetUpdateCartBackendStatusPayload>,
    ) => {
      state.backendCartUpdateStatus = action.payload.status;
    },
    refreshCartItemsValidity: (state, action: PayloadAction<RefreshCartItemsValidityPayload>) => {
      const { items, areCartItemsValid } = action.payload;
      state.cartItems = items;
      state.areCartItemsValid = areCartItemsValid;
    },
    updateCartState: (state, action: PayloadAction<UpdateCartBackendPayload>) => {
      let cartUpdateObject: CartItem;
      let index: number;
      state.timezone = dayjs.tz.guess();
      switch (action.payload.actionType) {
        case CartOperationType.ADD:
          cartUpdateObject = action.payload.cartUpdateObject as CartItem;
          state.cartItems.push(cartUpdateObject);
          break;
        case CartOperationType.UPDATE:
          cartUpdateObject = action.payload.cartUpdateObject as CartItem;
          index = Number(action.payload.index);
          if (index >= 0 && index < state.cartItems.length) {
            state.cartItems[index] = cartUpdateObject;
          }
          break;
        case CartOperationType.REMOVE:
          index = Number(action.payload.index);
          if (index >= 0 && index < state.cartItems.length) {
            state.cartItems.splice(index, 1);
          }
          break;
        default:
          logger('error', 'Invalid action type for updation of cart');
      }
    },
    updateCartBackendFulfilled: (state) => {
      logger('info', 'Successfully updated backend cart');
      state.backendCartUpdateStatus = null;
    },
    updateCartBackendRejected: (state) => {
      logger('error', 'Error in updating backend cart');
      state.backendCartUpdateStatus = null;
    },
    startLoadingQuote: (state) => {
      state.isLoadingFlags.quote = true;
    },
    errorLoadingQuote: (state) => {
      state.isLoadingFlags.quote = false;
      state.quote = undefined;
    },
    quoteLoaded: (state, action: PayloadAction<{ quote: Quote }>) => {
      state.isLoadingFlags.quote = false;
      state.quote = action.payload.quote;
    },
    updateShift: (state, action: PayloadAction<UpdateShiftPayload>) => {
      state.shift = {
        ...state.shift,
        ...action.payload.shift,
      };
    },
    updateActiveShift: (state, action: PayloadAction<UpdateActiveShiftPayload>) => {
      if (state.shift) {
        state.shift.active = action.payload.active;
      }
    },
    updateRentalProtection: (state, action: PayloadAction<UpdateRentalProtectionPayload>) => {
      state.rentalProtection = {
        ...state.rentalProtection,
        ...action.payload.rentalProtection,
      };
    },
    updateActiveRentalProtection: (
      state,
      action: PayloadAction<UpdateActiveRentalProtectionPayload>,
    ) => {
      if (state.rentalProtection) {
        state.rentalProtection.active = action.payload.active;
      }
    },
    updateSalesStaffData: (state, action: PayloadAction<SalesStaffDetails | null>) => {
      state.salesStaffDetails = action.payload;
    },
    setOrderPlacedForCurrentCart: (state, action: PayloadAction<{ value: boolean }>) => {
      state.orderPlacedForCurrentCart = action.payload.value;
    },
    setSiteLevelDate: (
      state,
      action: PayloadAction<Omit<SiteLevelDate, 'updatedOn' | 'state'>>,
    ) => {
      state.siteLevelDate = {
        ...action.payload,
        updatedOn: dayjs().unix(),
      };
    },
    setSiteLevelStartDate: (state, action: PayloadAction<number>) => {
      state.siteLevelDate = {
        ...state.siteLevelDate,
        start: action.payload,
        updatedOn: dayjs().unix(),
      };
    },
    setSiteLevelEndDate: (state, action: PayloadAction<number>) => {
      state.siteLevelDate = {
        ...state.siteLevelDate,
        end: action.payload,
        updatedOn: dayjs().unix(),
      };
    },
    resetSiteLevelDate: (state) => {
      state.siteLevelDate = undefined;
    },
    validateAndUpdateDates: (state, action: PayloadAction<ValidateCartDatesPayload>) => {
      const { isSiteLevelDateChange, startDate, endDate } = action.payload;

      const updatedStartDate = isSiteLevelDateChange ? state.siteLevelDate?.start : startDate;
      const updatedEndDate = isSiteLevelDateChange ? state.siteLevelDate?.end : endDate;

      if (!updatedStartDate || !updatedEndDate) return;

      if (!isSiteLevelDateChange) {
        state.siteLevelDate = {
          start: updatedStartDate,
          end: updatedEndDate,
          updatedOn: dayjs().unix(),
        };
      }
      state.cartItems.forEach((cartItem) => {
        cartItem.startDate = updatedStartDate as number;
        cartItem.endDate = updatedEndDate as number;
      });
    },
    loadingIsTaxExempt: (state) => {
      state.isLoadingFlags.isTaxExempt = true;
    },
    setIsTaxExempt: (state, action: PayloadAction<boolean>) => {
      state.isLoadingFlags.isTaxExempt = false;
      state.isTaxExempt = action.payload;
    },
    clearCart: (state) => {
      return { ...initialState, siteLevelDate: state.siteLevelDate };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateCartItemsData.pending, (state) => {
        state.loading = CartStateLoadingType.PENDING;
      })
      .addCase(validateCartItemsData.fulfilled, (state, action) => {
        state.loading = CartStateLoadingType.SUCCEEDED;
        state.cartItems = action.payload as CartItem[];
      })
      .addCase(validateCartItemsData.rejected, (state, action) => {
        state.loading = CartStateLoadingType.FAILED;
        logger('error', action.error.message);
      });
  },
});

export const {
  openCartModal,
  closeCartModal,
  toggleCartHeaderDropdown,
  setUserUuid,
  setUpdateCartBackendStatus,
  refreshCartItemsValidity,
  updateCartState,
  updateCartBackendFulfilled,
  updateCartBackendRejected,
  startLoadingQuote,
  quoteLoaded,
  errorLoadingQuote,
  updateShift,
  updateActiveShift,
  updateRentalProtection,
  updateActiveRentalProtection,
  updateSalesStaffData,
  setOrderPlacedForCurrentCart,
  setSiteLevelStartDate,
  setSiteLevelEndDate,
  setSiteLevelDate,
  resetSiteLevelDate,
  validateAndUpdateDates,
  loadingIsTaxExempt,
  setIsTaxExempt,
  clearCart,
} = cartSlice.actions;

export const updateCartBackendEpicAction = createAction<UpdateCartBackendPayload>(
  'cart/updateBackendCartEpicAction',
);

export default cartSlice.reducer;
